<template>
    <div>
        <v-card class="price-calculation-form" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col class="d-flex align-center" cols="12" sm="9">
                        <v-btn dark icon @click="goBack">
                            <v-icon>fal fa-arrow-left</v-icon>
                        </v-btn>
                        <div class="ml-3">{{ $t('price_calculations.heading') }}</div>
                    </v-col>

                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            v-if="!disabled && !form.recorded_at"
                            dark
                            outlined
                            :disabled="!form.items.length"
                            @click="save(false)"
                        >
                            <v-icon left>fal fa-check</v-icon>
                            {{ $t('price_calculations.save') }}
                        </v-btn>

                        <v-btn
                            v-if="!disabled && !form.recorded_at"
                            class="mx-3"
                            dark
                            outlined
                            :disabled="!form.items.length"
                            @click="record"
                        >
                            <v-icon left>fal fa-file-check</v-icon>
                            {{ $t('price_calculations.record') }}
                        </v-btn>

                        <v-btn
                            v-if="form.recorded_at"
                            class="mx-3"
                            dark
                            outlined
                            :disabled="!form.items.length"
                            @click="rollback"
                        >
                            <v-icon left>fal fa-file-times</v-icon>
                            {{ $t('price_calculations.rollback') }}
                        </v-btn>

                        <v-menu bottom transition="slide-y-transition">
                            <template v-slot:activator="{ on }">
                                <v-btn dark v-on="on" icon>
                                    <v-icon>far fa-ellipsis-v</v-icon>
                                </v-btn>
                            </template>

                            <v-list>
                                <v-list-item
                                    key="print"
                                    :disabled="!priceCalculation || !priceCalculation.recorded_at"
                                    @click="print"
                                >
                                    <v-list-item-action class="mx-3">
                                        <v-icon :disabled="!priceCalculation || !priceCalculation.recorded_at">
                                            fal fa-print
                                        </v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title>
                                        {{ $t('general.print') }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <v-row class="mt-3 mx-0">
                    <v-col cols="12" md="3">
                        <div class="highlight-section">
                            <div class="section-title">
                                <v-icon class="mr-3">fal fa-file-alt</v-icon>
                                {{ $t('price_calculations.section_document_info') }}
                            </div>

                            <div class="section-content">
                                <v-row>
                                    <v-col cols="6">
                                        <v-menu
                                            v-model="picker1"
                                            :close-on-content-click="false"
                                            :nudge-right="32"
                                            transition="scale-transition"
                                            min-width="290"
                                            offset-y
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="form.date"
                                                    :error-messages="form.$errors.date"
                                                    :label="$t('general.date')"
                                                    prepend-icon="fal fa-calendar"
                                                    dense
                                                    readonly
                                                    v-on="on"
                                                    :disabled="disabled || !!form.items.length"
                                                    @blur="form.validate('date')"
                                                    @input="delete form.$errors.date"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="form.date"
                                                @input="picker1 = false; form.validate('date')"
                                            />
                                        </v-menu>
                                    </v-col>

                                    <v-col cols="6">
                                        <v-text-field
                                            v-model="form.number"
                                            :label="$t('price_calculations.number')"
                                            dense
                                            :disabled="disabled"
                                            :error-messages="form.$errors.number"
                                            @blur="form.validate('number')"
                                            @input="delete form.$errors.number"
                                        />
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12">
                                        <v-textarea
                                            v-model="form.description"
                                            class="ma-0"
                                            :label="$t('general.description')"
                                            dense
                                            auto-grow
                                            :rows="1"
                                            :error-messages="form.$errors.description"
                                            :disabled="disabled"
                                            @blur="form.validate('description')"
                                            @input="delete form.$errors.description"
                                        />
                                    </v-col>
                                </v-row>
                            </div>
                        </div>
                    </v-col>

                    <v-col cols="12" md="5">
                        <div class="highlight-section">
                            <div class="section-title">
                                <v-icon class="mr-3">fal fa-file-invoice-dollar</v-icon>
                                {{ $t('price_calculations.section_supplier_invoice') }}
                            </div>

                            <div class="section-content">
                                <v-row>
                                    <v-col cols="8">
                                        <v-autocomplete
                                            v-model="form.client_id"
                                            :items="clients"
                                            item-text="name"
                                            item-value="id"
                                            :filter="filterClient"
                                            dense
                                            :hint="$t('price_calculations.supplier_hint')"
                                            :persistent-hint="!form.client_id"
                                            :label="$tc('general.supplier', 1)"
                                            menu-props="offsetY"
                                            auto-select-first
                                            :error-messages="form.$errors.client_id"
                                            :disabled="disabled"
                                            @blur="form.validate('client_id')"
                                            @input="delete form.$errors.client_id"
                                        />
                                    </v-col>

                                    <v-col cols="4">
                                        <v-text-field
                                            v-model="form.invoice_number"
                                            :label="$t('price_calculations.invoice_number')"
                                            dense
                                            :error-messages="form.$errors.invoice_number"
                                            :disabled="disabled"
                                            @blur="form.validate('invoice_number')"
                                            @input="delete form.$errors.invoice_number"
                                        />
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="4">
                                        <v-menu
                                            v-model="picker2"
                                            :close-on-content-click="false"
                                            :nudge-right="32"
                                            transition="scale-transition"
                                            min-width="290"
                                            offset-y
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="form.invoice_date"
                                                    :error-messages="form.$errors.invoice_date"
                                                    :label="$t('price_calculations.invoice_date')"
                                                    prepend-icon="fal fa-calendar"
                                                    dense
                                                    readonly
                                                    v-on="on"
                                                    :disabled="disabled"
                                                    @blur="form.validate('invoice_date')"
                                                    @input="delete form.$errors.invoice_date"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="form.invoice_date"
                                                @input="picker2 = false; form.validate('invoice_date')"
                                            />
                                        </v-menu>
                                    </v-col>

                                    <v-col cols="4">
                                        <v-menu
                                            v-model="picker3"
                                            :close-on-content-click="false"
                                            :nudge-right="32"
                                            transition="scale-transition"
                                            min-width="290"
                                            offset-y
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="form.invoice_due_date"
                                                    :error-messages="form.$errors.invoice_due_date"
                                                    :label="$t('price_calculations.invoice_due_date')"
                                                    prepend-icon="fal fa-calendar"
                                                    dense
                                                    readonly
                                                    v-on="on"
                                                    :disabled="disabled"
                                                    @blur="form.validate('invoice_due_date')"
                                                    @input="delete form.$errors.invoice_due_date"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="form.invoice_due_date"
                                                @input="picker3 = false; form.validate('invoice_due_date')"
                                            />
                                        </v-menu>
                                    </v-col>

                                    <v-col cols="4">
                                        <v-menu
                                            v-model="picker4"
                                            :close-on-content-click="false"
                                            :nudge-right="32"
                                            transition="scale-transition"
                                            min-width="290"
                                            offset-y
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="form.invoice_paid_date"
                                                    :error-messages="form.$errors.invoice_paid_date"
                                                    :label="$t('price_calculations.invoice_paid_date')"
                                                    prepend-icon="fal fa-calendar"
                                                    dense
                                                    readonly
                                                    v-on="on"
                                                    :disabled="disabled"
                                                    @blur="form.validate('invoice_paid_date')"
                                                    @input="delete form.$errors.invoice_paid_date"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="form.invoice_paid_date"
                                                @input="picker4 = false; form.validate('invoice_paid_date')"
                                            />
                                        </v-menu>
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="3">
                                        <number-field
                                            v-model="form.invoice_price_total"
                                            :label="$t('price_calculations.invoice_value')"
                                            :precision="2"
                                            dense
                                            :disabled="disabled || !!form.items.length"
                                            :error-messages="form.$errors.invoice_price_total"
                                            @blur="form.validate('invoice_price')"
                                            @input="delete form.$errors.invoice_price_total"
                                        />
                                    </v-col>

                                    <v-col cols="3">
                                        <number-field
                                            v-model="form.dependent_costs_total"
                                            :label="$t('price_calculations.dependent_costs')"
                                            :precision="2"
                                            dense
                                            :disabled="disabled || !!form.items.length"
                                            :error-messages="form.$errors.dependent_costs_total"
                                            @blur="form.validate('dependent_costs')"
                                            @input="delete form.$errors.dependent_costs_total"
                                        />
                                    </v-col>

                                    <v-col cols="3">
                                        <v-tooltip
                                            nudge-top="20"
                                            nudge-left="80"
                                            bottom
                                            :disabled="disabled || !!form.items.length"
                                        >
                                            <template #activator="{ on }">
                                                <div class="mb-n2" v-on="on">
                                                    <v-checkbox
                                                        v-model="form.invoice_with_tax"
                                                        :label="$t('price_calculations.invoice_with_tax')"
                                                        :disabled="disabled || !!form.items.length"
                                                        :hint="$t('price_calculations.invoice_with_tax_description')"
                                                        dense
                                                    />
                                                </div>
                                            </template>
                                            <span>{{ $t('price_calculations.invoice_with_tax_description') }}</span>
                                        </v-tooltip>
                                    </v-col>

                                    <v-col cols="3">
                                        <v-checkbox
                                            v-model="form.create_invoice"
                                            :label="$t('price_calculations.create_invoice')"
                                            :disabled="isCreateInvoiceDisabled"
                                            dense
                                        />
                                    </v-col>

                                </v-row>
                            </div>
                        </div>
                    </v-col>

                    <v-col cols="12" md="4">
                        <div class="highlight-section">
                            <div class="section-title mb-4">
                                <v-icon class="mr-3">fal fa-sigma</v-icon>
                                {{ $t('price_calculations.section_summary') }}
                            </div>

                            <div class="section-content">
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('price_calculations.invoice_value') }}:</span>
                                    <span>{{ priceCalculationInternal.getInvoiceValue().toFormat('0.00') }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('price_calculations.dependent_costs') }}:</span>
                                    <span>{{ priceCalculationInternal.getDependentCosts().toFormat('0.00') }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('price_calculations.purchase_value') }}:</span>
                                    <span>{{ priceCalculationInternal.getPurchaseValue().toFormat('0.00') }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('price_calculations.purchase_tax') }}:</span>
                                    <span>{{ priceCalculationInternal.getPurchaseTax().toFormat('0.00') }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('price_calculations.purchase_value_after_tax') }}:</span>
                                    <span>{{ priceCalculationInternal.getPurchaseValueAfterTax().toFormat('0.00') }}</span>
                                </div>

                                <v-divider class="my-2" />

                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('price_calculations.price_difference') }}:</span>
                                    <span>{{ priceCalculationInternal.getPriceDifference().toFormat('0.00') }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('price_calculations.selling_value') }}:</span>
                                    <span>{{ priceCalculationInternal.getSellingValue().toFormat('0.00') }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('price_calculations.tax') }}:</span>
                                    <span>{{ priceCalculationInternal.getTax().toFormat('0.00') }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('price_calculations.selling_value_after_tax') }}:</span>
                                    <span>{{ priceCalculationInternal.getSellingValueAfterTax().toFormat('0.00') }}</span>
                                </div>
                            </div>
                        </div>
                    </v-col>
                </v-row>

                <v-data-table
                    class="items-table operations striped"
                    :headers="headers"
                    :items="form.items"
                    :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
                    :mobile-breakpoint="0"
                    dense
                >
                    <template v-slot:header.actions="{ on, props }">
                        <div v-if="!disabled" class="d-flex justify-end">
                            <v-btn icon large color="primary" @click="openItemForm()">
                                <v-icon>fal fa-plus</v-icon>
                            </v-btn>
                        </div>
                    </template>

                    <template v-slot:item="{ item, index }">
                        <tr>
                            <td>{{ index + 1 }}</td>
                            <td>{{ item.getProduct() && item.getProduct().code ? item.getProduct().code : '' }}</td>
                            <td>{{ item.getProduct() ? item.getProduct().name : '' }}</td>
                            <td>{{ item.getQuantity() }} <small>{{ item.getUnitOfMeasure() }}</small></td>
                            <td>{{ item.getInvoicePrice().toFormat('0.00') }}</td>
                            <td>{{ item.getInvoiceDiscountPercent() }}</td>
                            <td>{{ item.getInvoicePriceAfterDiscount().toFormat('0.00') }}</td>
                            <td>{{ item.getInvoiceValue().toFormat('0.00') }}</td>
                            <td>{{ item.getDependentCosts().toFormat('0.00') }}</td>
                            <td class="error--text font-weight-bold">{{ item.getPurchaseValue().toFormat('0.00') }}</td>
                            <td class="warning--text font-weight-bold">{{ item.getPriceDifference().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingValue().toFormat('0.00') }}</td>
                            <td>{{ item.getTax().toFormat('0.00') }}  <small>({{ item.getTaxRate() }}%)</small></td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingValueAfterTax().toFormat('0.00') }}</td>

                            <td class="separator">{{ item.getPurchasePrice().toFormat('0.00') }}</td>
                            <td>{{ item.getMarginPercent() }}</td>
                            <td>{{ item.getPriceDifferencePerUnit().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingPrice().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingPriceAfterTax().toFormat('0.00') }}</td>

                            <td align="center" class="w35 maxw60">
                                <v-menu
                                    v-if="!disabled"
                                    bottom
                                    transition="slide-y-transition"
                                >
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item :key="1" @click="openItemForm(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item :key="2" @click="deleteItem(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon class="error--text">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title class="error--text">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Item form -->
        <price-calculation-item-edit
            :show.sync="showItemForm"
            :item="selectedItem"
            :products="products"
            :pricelists="pricelists"
            :invoice-price="form.invoice_price_total"
            :dependent-costs="form.dependent_costs_total"
            :invoice-with-tax="form.invoice_with_tax"
            :date="form.date"
            :invoice-date="form.invoice_date"
            @save="onItemSave"
        />
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import ApiService from '@/services/ApiService';
import Product from '@/models/Product';
import PriceCalculation from '@/models/PriceCalculation';
import PriceCalculationItemEdit from '@/views/shared/admin/priceCalculations/PriceCalculationItemEdit';
import { required } from 'vuelidate/lib/validators';
import HtmlGen from '@/wrappers/HtmlGen';
import Client from '@/models/Client';
import NumberField from '@/components/NumberField';

export default {
    components: {
        NumberField,
        PriceCalculationItemEdit
    },

    async created() {
        document.addEventListener('keyup', this.backgroundKeyUpHandler);

        let url = '/admin/load/price-calculation' + (this.$route.params.id ? '/' + this.$route.params.id : '');

        const data = await loadData(url);

        if(data.priceCalculation) {
            this.priceCalculation = new PriceCalculation(data.priceCalculation);
            this.order = data?.priceCalculation?.order ?? null;
        }

        this.nextDocumentNumber = data.maxDocumentNumber ? parseInt(data.maxDocumentNumber) + 1 : 1;
        this.clients = data.clients.map(c => new Client(c));
        this.products = data.products.map(p => new Product(p));
        this.pricelists = data.pricelists;
        this.form = this.createForm();
    },

    beforeDestroy() {
        document.removeEventListener('keyup', this.backgroundKeyUpHandler);
    },

    data() {
        return {
            priceCalculation: null,
            nextDocumentNumber: null,
            products: [],
            pricelists: [],
            clients: [],
            order: null,
            form: this.createForm(),
            showItemForm: false,
            headers: [
                { text: this.$t('general.number'), value: 'number', sortable: false },
                { text: this.$t('general.code'), value: 'product.code', sortable: false },
                { text: this.$t('general.inventory_item'), value: 'product.name', sortable: false },
                { text: this.$t('price_calculations.quantity'), value: 'quantity', sortable: false },
                { text: this.$t('price_calculations.invoice_price'), value: 'invoice_price', sortable: false },
                { text: this.$t('price_calculations.invoice_discount_percent'), value: 'invoice_discount_percent', sortable: false },
                { text: this.$t('price_calculations.invoice_price_after_discount'), value: 'invoice_price_after_discount', sortable: false },
                { text: this.$t('price_calculations.invoice_value'), value: 'invoice_value', sortable: false },
                { text: this.$t('price_calculations.dependent_costs'), value: 'dependent_costs', sortable: false },
                { text: this.$t('price_calculations.purchase_value'), value: 'purchase_value', sortable: false },
                { text: this.$t('price_calculations.price_difference'), value: 'price_difference', sortable: false },
                { text: this.$t('price_calculations.selling_value'), value: 'selling_value', sortable: false },
                { text: this.$t('price_calculations.tax'), value: 'tax', sortable: false },
                { text: this.$t('price_calculations.selling_value_after_tax'), value: 'selling_value_after_tax', sortable: false },
                { text: this.$t('price_calculations.purchase_price'), value: 'purchase_price', sortable: false, class: 'separator' },
                { text: this.$t('price_calculations.margin_percent'), value: 'margin_percent', sortable: false },
                { text: this.$t('price_calculations.price_difference_per_unit'), value: 'price_difference_per_unit', sortable: false },
                { text: this.$t('price_calculations.selling_price'), value: 'selling_price', sortable: false },
                { text: this.$t('price_calculations.selling_price_after_tax'), value: 'selling_price_after_tax', sortable: false },
                { text: '', value: 'actions', sortable: false }
            ],

            selectedItem: null,
            picker1: false,
            picker2: false,
            picker3: false,
            picker4: false,
            calculationNumberPrefix: moment().format('YY/'),
        };
    },

    validations: {
        form: {
            number: { required },
            date: { required },
            client_id: { required },
            invoice_number: { required },
            invoice_date: { required },
            invoice_price_total: { required },
            dependent_costs_total: { required },
        }
    },

    computed: {
        priceCalculationInternal() {
            return new PriceCalculation(this.form.toJson());
        },

        disabled() {
            return !!this.form.recorded_at;
        },

        isCreateInvoiceDisabled() {
            return !!this.priceCalculation?.id;
        },
    },

    methods: {
        createForm() {
            return new Form(this, 'form', {
                id: this.priceCalculation?.id || null,
                number: this.priceCalculation?.number || this.calculationNumberPrefix + this.nextDocumentNumber,
                date: this.priceCalculation?.date
                    ? moment(this.priceCalculation.date).format('YYYY-MM-DD')
                    : moment().format('YYYY-MM-DD'),
                user_id: this.user?.id || null,
                client_id: this.priceCalculation?.client_id || null,
                invoice_number: this.priceCalculation?.invoice_number || null,
                invoice_date: this.priceCalculation?.invoice_date
                    ? moment(this.priceCalculation.invoice_date).format('YYYY-MM-DD')
                    : moment().format('YYYY-MM-DD'),
                invoice_due_date: this.priceCalculation?.invoice_due_date
                    ? moment(this.priceCalculation.invoice_due_date).format('YYYY-MM-DD')
                    : null,
                invoice_paid_date: this.priceCalculation?.invoice_paid_date
                    ? moment(this.priceCalculation.invoice_paid_date).format('YYYY-MM-DD')
                    : null,
                invoice_price_total: this.priceCalculation?.invoice_price_total || null,
                dependent_costs_total: this.priceCalculation?.dependent_costs_total || '0.0000',
                description: this.priceCalculation?.description || null,
                invoice_with_tax: this.priceCalculation ? this.priceCalculation.invoice_with_tax : true,
                create_invoice: !!this.order && !!this.priceCalculation?.id,
                items: this.priceCalculation?.items || [],
                recorded_at: this.priceCalculation?.recorded_at || null,
            });
        },

        openItemForm(item = null) {
            if(!this.form.validate()) return;

            this.selectedItem = item;
            this.showItemForm = true;
        },

        deleteItem(item) {
            this.form.items = this.form.items.filter(i => i !== item);
            this.showItemForm = false;
        },

        onItemSave(item) {
            let isEdit = this.form.items.some(i => i.hash === item.hash);
            if(!isEdit) {
                this.form.items.push(item);
                return;
            }

            this.form.items.forEach((i, index, array) => {
                if (i.hash === item.hash) {
                    this.$set(array, index, item);
                }
            });
        },

        async save(recordPending = false) {
            if(!this.form.validate()) return;

            if (!this.form.id) {
                const response = await ApiService.post('/admin/price-calculations', this.form);
                this.priceCalculation = new PriceCalculation(response.data);
                this.form = this.createForm();

                if(recordPending) return;

                this.$snackbar(this.$t('price_calculations.saved'), 'success');
            } else {
                const response = await ApiService.patch(`/admin/price-calculations/${this.form.id}`, this.form).catch(err => {
                    console.log(err);
                });
                this.priceCalculation = new PriceCalculation(response.data);
                this.form = this.createForm();

                if(recordPending) return;

                this.$snackbar(this.$t('price_calculations.updated'), 'success');
            }

            this.goBack();
        },

        async record() {
            await this.save(true);

            const response = await ApiService.post(`/admin/price-calculations/${this.form.id}/record`, {})
            if(response.data.products?.length) {
                Product.insert({ data: response.data.products, freeze: true });
            }

            this.$snackbar(this.$t('price_calculations.recorded'), 'success');

            this.goBack();
        },

        async rollback() {
            if(!this.form.id) return;

            const response = await ApiService.post(`/admin/price-calculations/${this.form.id}/rollback`, {})
            if(response.data.products?.length) {
                Product.insert({ data: response.data.products, freeze: true });
            }

            this.$snackbar(this.$t('price_calculations.rolled_back'), 'success');

            this.goBack();
        },

        print() {
            const html = this.priceCalculation.toTextLines().map(line => HtmlGen.fromTextLine(line)).join('');
            this.$printService.printCustom(null, html, null, { choosePrinter: true })
        },

        goBack() {
            this.$router.push({ name: 'adminPriceCalculations' }).catch(err => {});
        },

        filterClient(item, queryText, itemText) {
            return item.matches(queryText);
        },

        backgroundKeyUpHandler(event) {
            if(event.keyCode != 187) return;
            if(this.showItemForm) return;

            this.openItemForm();
        },
    }
};
</script>

<style lang="scss" scoped>
.price-calculation-form {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    .highlight-section {
        border-radius: 3px;
        background: #fafafa;
        border: 1px solid #cccccc;
        padding: 20px;
        min-height: 295px;
        font-size: 15px;

        .section-title {
            margin-bottom: 24px;
            font-size: 20px;
            color: black;

            .v-icon {
                color: black;
            }
        }
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }
    }
}
</style>
